import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/** Critères portés par l'URL, avec le filtre fermé de l'écran concerné. */
export interface CriteresUrl<F extends string> {
  readonly recherche: string;
  readonly filtre: F | '';
  readonly page: number;
}

/**
 * Les critères de recherche vivent dans l'URL — source unique.
 *
 * Conséquences voulues : une liste filtrée se partage par son adresse, le bouton
 * Retour défait un filtre, et un rechargement ne perd rien. La page est retirée
 * de l'URL quand on revient à la première, pour ne pas laisser « page=1 ».
 *
 * Cette version est GÉNÉRIQUE : le nom du paramètre et la liste fermée de ses
 * valeurs viennent de l'écran. Une seconde copie de cette mécanique finirait par
 * se comporter autrement d'un écran à l'autre.
 */
export function useCriteresUrlGenerique<F extends string>(
  nomFiltre: string,
  valeurs: readonly F[],
): CriteresUrl<F> & {
  setRecherche: (valeur: string) => void;
  setFiltre: (valeur: F | '') => void;
  setPage: (valeur: number) => void;
  toutEffacer: () => void;
} {
  const [parametres, setParametres] = useSearchParams();

  const criteres = useMemo<CriteresUrl<F>>(() => {
    const recherche = parametres.get('recherche')?.trim() ?? '';
    const filtreBrut = parametres.get(nomFiltre) ?? '';
    const filtre = valeurs.includes(filtreBrut as F) ? (filtreBrut as F) : '';
    const pageBrute = Number.parseInt(parametres.get('page') ?? '1', 10);
    const page = Number.isFinite(pageBrute) && pageBrute > 0 ? pageBrute : 1;

    return { recherche, filtre, page };
  }, [parametres, nomFiltre, valeurs]);

  const ecrire = useCallback(
    (changements: { recherche?: string; filtre?: F | ''; page?: number }, remplacer: boolean) => {
      const suivants = new URLSearchParams(parametres);
      const appliquer = (cle: string, valeur: string) => {
        if (valeur === '') suivants.delete(cle);
        else suivants.set(cle, valeur);
      };

      if (changements.recherche !== undefined) appliquer('recherche', changements.recherche);
      if (changements.filtre !== undefined) appliquer(nomFiltre, changements.filtre);
      if (changements.page !== undefined) {
        appliquer('page', changements.page > 1 ? String(changements.page) : '');
      }

      // Tout changement de critère ramène à la première page : rester en page 3
      // d'un ancien filtre afficherait une page vide alors que des résultats existent.
      if (changements.recherche !== undefined || changements.filtre !== undefined) {
        if (changements.page === undefined) suivants.delete('page');
      }

      setParametres(suivants, { replace: remplacer });
    },
    [parametres, setParametres, nomFiltre],
  );

  return {
    ...criteres,
    // La frappe remplace l'entrée d'historique : sans cela, chaque mot temporisé
    // empilerait un Retour à défaire.
    setRecherche: useCallback((valeur: string) => ecrire({ recherche: valeur }, true), [ecrire]),
    setFiltre: useCallback(
      (valeur: F | '') => ecrire({ filtre: valeur, page: 1 }, false),
      [ecrire],
    ),
    setPage: useCallback((valeur: number) => ecrire({ page: valeur }, false), [ecrire]),
    toutEffacer: useCallback(
      () => setParametres(new URLSearchParams(), { replace: false }),
      [setParametres],
    ),
  };
}
